/* @flow */

import * as React from 'react';
import { observable, action } from 'mobx';
import { inject, observer } from 'mobx-react';
import { translate } from 'react-i18next';
import type { TranslatorProps } from 'react-i18next';

import AutoCompleteModel from 'models/forms/AutoCompleteModel.js';
import ActionDrawerBlock from 'views/ActionDrawer/components/ActionDrawerBlock/ActionDrawerBlock';
import ActionDrawerFormButtons from 'views/ActionDrawer/components/ActionDrawerFormButtons/ActionDrawerFormButtons';
import ElectroSelectExtra from 'forms/components/inputs/ElectroSelectExtra/ElectroSelectExtra';
import ElectroDatePicker from 'forms/components/inputs/ElectroDatePicker/ElectroDatePicker';
import ElectroTextField from 'forms/components/inputs/ElectroTextField/ElectroTextField';
import type TypeActionDrawerStore from 'stores/ActionDrawerStore.js';
import styles from 'views/ActionDrawer/ActionDrawer.module.scss';
import type Form from 'forms/_.extend';

import {
  autoCompleteAccountCompanies,
  autoCompleteCompanySites,
  autoCompleteBuildings,
  autoCompleteAccountUsers,
} from 'api/forms.js';

type TypeProps = {
  project: Object,
  form: Form,
  prefillFields: Object,
  actionDrawerStore: TypeActionDrawerStore,
} & TranslatorProps;

@translate()
@inject('actionDrawerStore')
@observer
export default class AddNewProjectForm extends React.Component<*> {
  @observable usersModelLead: AutoCompleteModel = new AutoCompleteModel(autoCompleteAccountUsers);

  @observable usersModelEngineers: AutoCompleteModel = new AutoCompleteModel(
    autoCompleteAccountUsers
  );

  @observable companyModel: AutoCompleteModel = new AutoCompleteModel(
    autoCompleteAccountCompanies()
  );

  @observable sitesModel: AutoCompleteModel = new AutoCompleteModel(autoCompleteCompanySites);

  @observable buildingsModel: AutoCompleteModel = new AutoCompleteModel(autoCompleteBuildings);

  @observable roomsModel: AutoCompleteModel = new AutoCompleteModel(autoCompleteBuildings);

  @observable systemsModel: AutoCompleteModel = new AutoCompleteModel(autoCompleteBuildings);

  @observable localState = {
    selectedValueEn: '',
  };

  /**
   * Constructor
   * @param props
   */
  constructor(props: Object) {
    super(props);
    this.props.accountsModel.search = '';
    this.replaceModels();
  }

  /**
   * Component did mount hook
   */
  componentDidMount(): void {
    this.prefillFields();
  }

  /**
   * Pre-fill fields
   * Allows us to pre-fill fields based on passed in values
   * from the prefillFields prop
   */
  @action
  prefillFields() {
    const { form, prefillFields }: TypeProps = this.props;

    for (let field in prefillFields) {
      const fillFromField = prefillFields[field];

      form.$(field).set('value', fillFromField.value);

      if (form.$(field).extra) {
        form.$(field).set('extra', {
          ...form.$(field).extra,
          ...fillFromField.extra,
          actionButton: false,
          childExtraFilters: form.$(field).extra.childExtraFilters,
        });
      }
    }

    const prefilledFields = prefillFields['addNewProject.projectEngineer'];
    if (prefilledFields) {
      this.localState.selectedValueEn = prefilledFields.value;
    }
  }

  /**
   * Replace models
   * From our replace models prop, we swap out to the relevant ones,
   * this way we can stay reactive by changing the observable
   */
  replaceModels() {
    const { replaceModels }: TypeProps = this.props;

    if (replaceModels['location.locationCompany'] && replaceModels['location.locationSite']) {
      this.companyModel = replaceModels['location.locationCompany'];
      this.sitesModel = replaceModels['location.locationSite'];
    }
  }

  /**
   * On change event
   * @param selectedValue
   */
  handleOnChangeEngineer = (selectedValue: string) => {
    this.localState.selectedValueEn = selectedValue;
  };

  render(): React.Node {
    const { t }: TranslatorProps = this.props;
    const { form }: TypeProps = this.props;
    const usersModelLead: Object = this.usersModelLead;
    const usersModelEngineers: Object = this.usersModelEngineers;
    const companyModel: Object = this.companyModel;
    const sitesModel: Object = this.sitesModel;
    const buildingsModel: Object = this.buildingsModel;
    const roomsModel: Object = this.roomsModel;
    const systemsModel: Object = this.systemsModel;

    return (
      <React.Fragment>
        <div className={styles['action-drawer-content']}>
          <ActionDrawerBlock
            divider={true}
            gutters={true}
            heading={t('general:projects.labels.details')}
          >
            <div className={styles['form-group']}>
              <ElectroTextField field={form.$('addNewProject.projectName')} />
              <ElectroTextField field={form.$('addNewProject.projectCode')} />
              <ElectroTextField
                field={form.$('addNewProject.projectDescription')}
                props={{
                  multiline: true,
                  rows: 3,
                }}
              />
              <ElectroDatePicker field={form.$('addNewProject.projectStartDate')} />
              <ElectroDatePicker field={form.$('addNewProject.projectEndDate')} />
              <ElectroSelectExtra
                field={form.$('addNewProject.chooseLead')}
                form={form}
                formName="addNewProject"
                labelKey={['first_name', 'last_name']}
                props={{
                  model: usersModelLead,
                }}
              />
            </div>
          </ActionDrawerBlock>
          <br />
          <ActionDrawerBlock
            divider={true}
            gutters={true}
            heading={t('general:actions.assign_project_engineer_plural')}
          >
            <div className={styles['form-group']}>
              <ElectroSelectExtra
                field={form.$('addNewProject.projectEngineer')}
                form={form}
                formName="assignProjectEngineers"
                labelKey={['first_name', 'last_name']}
                multi={true}
                handleOnChange={this.handleOnChangeEngineer}
                singleSelectedValue={this.localState.selectedValueEn}
                props={{
                  model: usersModelEngineers,
                }}
              />
            </div>
          </ActionDrawerBlock>
          <ActionDrawerBlock
            divider={false}
            gutters={true}
            display={this.props.hideSections.includes('location') ? 'none' : 'block'}
            heading={t('general:projects.labels.location')}
          >
            <div className={styles['form-group']}>
              <ElectroSelectExtra
                field={form.$('location.locationCompany')}
                form={form}
                formName="location"
                labelKey="name"
                actionButtonData={{
                  disabled: !form.$('addNewProject.tradingAccount').value,
                }}
                props={{
                  model: companyModel,
                  modelChild: sitesModel,
                }}
              />

              <ElectroSelectExtra
                field={form.$('location.locationSite')}
                form={form}
                formName="location"
                labelKey="name"
                actionButtonData={{
                  disabled: !form.$('addNewProject.tradingAccount').value,
                }}
                props={{
                  model: sitesModel,
                  modelChild: buildingsModel,
                }}
              />

              <ElectroSelectExtra
                field={form.$('location.locationBuilding')}
                form={form}
                formName="location"
                labelKey="name"
                actionButtonData={{
                  disabled: !form.$('addNewProject.tradingAccount').value,
                }}
                props={{
                  model: buildingsModel,
                  modelChild: roomsModel,
                }}
              />

              <ElectroSelectExtra
                field={form.$('location.locationRoom')}
                form={form}
                formName="location"
                labelKey="name"
                actionButtonData={{
                  disabled: !form.$('addNewProject.tradingAccount').value,
                }}
                props={{
                  model: roomsModel,
                  modelChild: systemsModel,
                }}
              />

              <ElectroSelectExtra
                field={form.$('location.locationSystem')}
                form={form}
                formName="location"
                labelKey="name"
                actionButtonData={{
                  disabled: !form.$('addNewProject.tradingAccount').value,
                }}
                props={{
                  model: systemsModel,
                }}
              />
            </div>
          </ActionDrawerBlock>
        </div>
        <ActionDrawerFormButtons form={form} submitTxt={t('general:actions.create')} />
      </React.Fragment>
    );
  }
}
